'use client';
import PropTypes from 'prop-types';
import axios from 'axios';
import useSWR, { unstable_serialize, useSWRConfig } from 'swr';
import { signIn, signOut } from 'next-auth/react';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import { shared } from '../../config';
import { useLocalStorage } from '../../hooks';
import { useMigratedCustomer } from '../../useMigratedCustomer';
import {
  SESSION_KEY,
  LOADING_STATUS,
  AUTHENTICATED_STATUS,
  UNAUTHENTICATED_STATUS,
} from './constants';
import { useSingleton } from './container';

const getCsrfToken = async () => {
  const response = await axios.get(`/api/auth/csrf/`);
  return response.data.csrfToken;
};

export const SessionContext = createContext(null);
export const useSession = () => useContext(SessionContext);
const EMPTY_SESSION = {
  data: undefined,
  error: undefined,
  isValidating: false,
  isLoading: false,
};

export class Session {
  constructor(cache, mutate) {
    this.cache = cache;
    this.mutate = mutate;
  }

  get _cache() {
    return this.cache.get(unstable_serialize(SESSION_KEY)) ?? EMPTY_SESSION;
  }

  get data() {
    return this._cache.data;
  }

  get user() {
    return this.data?.user;
  }

  get status() {
    const { data, error, isValidating } = this._cache;
    return !data && !error && isValidating
      ? LOADING_STATUS
      : data?.user
      ? AUTHENTICATED_STATUS
      : UNAUTHENTICATED_STATUS;
  }

  register = async (firstName, email, customerGroupId) => {
    const response = await axios.post(
      `/api/auth/callback/register/`,
      new URLSearchParams({
        ...(firstName ? { first_name: firstName } : {}),
        email,
        customer_group_id: customerGroupId,
        csrfToken: await getCsrfToken(),
        callbackUrl: String(window.location),
        json: 'true',
      }),
    );
    await this.mutate();

    return response;
  };

  login = async (email, password) => {
    const response = await axios.post(
      `/api/auth/callback/login/`,
      new URLSearchParams({
        email,
        password,
        csrfToken: await getCsrfToken(),
        callbackUrl: String(window.location),
        json: 'true',
      }),
    );
    await this.mutate();

    return response;
  };

  logout = async () => {
    const response = await axios.post(
      `/api/auth/signout/`,
      new URLSearchParams({
        csrfToken: await getCsrfToken(),
        callbackUrl: String(window.location),
        json: 'true',
      }),
    );
    await this.mutate();

    return response;
  };
}

const screeningFlows = ['weight_web_acquisition'];

export const SessionProvider = ({ children }) => {
  const router = useRouter();
  const [isAppSession, setIsAppSession] = useLocalStorage('isAppSession');
  const [screeningSession, setScreeningSession] =
    useLocalStorage('screeningSession');
  const isScreeningSession =
    screeningFlows.includes(router.query.flow) ||
    (Date.now() < screeningSession?.expire &&
      screeningFlows.includes(screeningSession?.flow));
  const [isRegistering, setIsRegistering] = useState(true);
  const { cache } = useSWRConfig();
  // this is preloaded on page load, so no need to revalidate
  // TODO(James) this isn't a generic solution to data being preloaded
  // how do we preload certain data and avoid revalidating on page load?
  const { mutate, data, error, isValidating } = useSWR(SESSION_KEY, {
    // Custom fetcher b/c this comes from ollieweb, not olliemain
    ...(shared.OLLIE_ENV !== 'storybook' && {
      fetcher: url => axios.get(url).then(resp => resp.data),
      revalidateOnMount: router.pathname === '/404',
    }),
  });

  const service = useSingleton(Session, () => new Session(cache, mutate));

  // **** TEMP DURING MIGRATION ONLY vvvvv

  const { fetchCustomer } = useMigratedCustomer(data?.user);

  useEffect(() => {
    data?.user?.id &&
      !data?.user?.access_token.includes('shcat') &&
      fetchCustomer().then(({ legacy, status }) => {
        if (
          status === 'lead' ||
          (legacy === false && !data?.user?.access_token.includes('shcat'))
        ) {
          service.logout().then(() => {
            signOut(
              status === 'lead' && {
                redirect: false,
                callbackUrl: window.location.href,
              },
            ).then(data => {
              window && router.push(data.url);
            });
          });
        }
      });
  }, [fetchCustomer, data?.user, service, router]);

  // **** END TEMP DURING MIGRATION ONLY ^^^^^

  /* there's a possibility where the JWT callback in nextauth cannot resolve a customer UUID.
  In that case, it may potentially cause an infinite redirect for the customer. To resolve it,
  signOut the customer completely, and send them back to Shopify login for a new token. */
  useEffect(() => {
    // this error is attached in the JWT callback if getCustomerUUID fails
    if (data?.error === 'CustomerUUIDError') {
      signOut({ redirect: false }).then(() => signIn('shopify'));
    }
  }, [data?.error]);

  // to ensure that the access_token in the swr cache is up to date
  // for a Shopify customer, we must revalidate on window/tab refocus
  useEffect(() => {
    const visibilityHandler = () =>
      document.visibilityState === 'visible' && mutate();
    window.addEventListener('visibilitychange', visibilityHandler, false);
    return () =>
      window.removeEventListener('visibilitychange', visibilityHandler, false);
  }, [mutate]);

  useEffect(() => {
    if (router.query.isAppSession) setIsAppSession(true);
    if (screeningFlows.includes(router.query.flow)) {
      const date = new Date();
      const expire = date.setDate(date.getDate() + 1);
      setScreeningSession({
        flow: router.query.flow,
        result: router.query.result,
        expire,
      });
    }
    if (router.query.firstName && router.query.email && !data) {
      setIsRegistering(true);
      service
        .register(router.query.firstName, router.query.email)
        .then(() => setIsRegistering(false));
    } else setIsRegistering(false);
  }, [
    data,
    router.query.email,
    router.query.firstName,
    router.query.flow,
    router.query.isAppSession,
    router.query.result,
    service,
    setIsAppSession,
    setScreeningSession,
  ]);

  const session = useMemo(
    () => ({
      data,
      error,
      isValidating,
      // Session service is mostly used by the API services that extend
      // BaseService from './service' or flows that need a live, mutable
      // reference to the underlying session data (e.g. register).
      service,
      status: service.status,
      user: service.user,
      register: service.register,
      login: service.login,
      logout: service.logout,
      isAppSession: Boolean(isAppSession || router.query.isAppSession),
      isScreeningSession,
      isRegistering,
    }),
    [
      data,
      error,
      isValidating,
      service,
      isAppSession,
      router.query.isAppSession,
      isScreeningSession,
      isRegistering,
    ],
  );

  return (
    <SessionContext.Provider value={session}>
      {children}
    </SessionContext.Provider>
  );
};
SessionProvider.propTypes = {
  children: PropTypes.node,
};
